/**
 * Popup document viewer for a run output artifact.
 * Modal body scrolls; wide tables also scroll horizontally inside the viewer.
 * Export PDF / Word uses the same normalized content as on-screen rendering.
 */

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Modal } from '@/components/ui/Modal';
import { exportStudioOutput } from '@/services/aiStudioAgentService';
import type { RunArtifact } from './artifacts';
import { JsonArtifactBody } from './JsonArtifactBody';
import { coerceArtifactTable } from './coerceArtifactTable';
import { buildArtifactExportDocument } from './buildArtifactExportDocument';
import { ArtifactBlocksBody } from './rendering/ArtifactBlocksBody';
import { asArtifactBlocks } from './rendering/artifactPrimitiveStyles';

interface ArtifactDocumentModalProps {
  artifact: RunArtifact | null;
  onClose: () => void;
}

function decisionLimitations(artifact: RunArtifact): string[] | undefined {
  const meta = artifact.body._decision;
  if (meta && Array.isArray(meta.limitations) && meta.limitations.length > 0) {
    return meta.limitations;
  }
  return undefined;
}

/**
 * Prefer structured rendering: document blocks → table coerce → text.
 */
function renderStructuredBody(artifact: RunArtifact): React.ReactNode {
  if (artifact.body.type === 'document') {
    return (
      <ArtifactBlocksBody
        blocks={artifact.body.blocks}
        limitations={decisionLimitations(artifact)}
      />
    );
  }
  if (artifact.body.type === 'json') {
    const data = artifact.body.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const blocks = asArtifactBlocks((data as { blocks?: unknown }).blocks);
      if (blocks.length > 0) {
        return (
          <ArtifactBlocksBody blocks={blocks} limitations={decisionLimitations(artifact)} />
        );
      }
    }
    return (
      <JsonArtifactBody data={artifact.body.data} limitations={decisionLimitations(artifact)} />
    );
  }
  if (artifact.body.type === 'text') {
    const rawText = artifact.body.text;
    // Text that is actually a JSON document/table (common VERIFIED research mistake)
    const trimmed = typeof rawText === 'string' ? rawText.trim() : '';
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (parsed && typeof parsed === 'object') {
          const obj = parsed as Record<string, unknown>;
          if (Array.isArray(obj['blocks']) && obj['blocks'].length > 0) {
            return (
              <ArtifactBlocksBody
                blocks={asArtifactBlocks(obj['blocks'])}
                limitations={decisionLimitations(artifact)}
              />
            );
          }
          return (
            <JsonArtifactBody data={parsed} limitations={decisionLimitations(artifact)} />
          );
        }
      } catch {
        /* fall through to text / table coerce */
      }
    }
    // If the "text" is actually a JSON table (common after re-propose), show as table.
    if (coerceArtifactTable(artifact.body.text)) {
      return (
        <JsonArtifactBody data={artifact.body.text} limitations={decisionLimitations(artifact)} />
      );
    }
    return (
      <pre className="max-h-[min(70vh,calc(90vh-10rem))] overflow-auto whitespace-pre-wrap text-base text-[var(--color-surface-text)]">
        {artifact.body.text}
      </pre>
    );
  }
  return null;
}

export const ArtifactDocumentModal: React.FC<ArtifactDocumentModalProps> = ({
  artifact,
  onClose,
}) => {
  const { t } = useLanguage();
  const open = artifact != null;
  const [exporting, setExporting] = useState<'pdf' | 'docx' | null>(null);
  const [exportError, setExportError] = useState(false);

  const handleExport = async (format: 'pdf' | 'docx') => {
    if (!artifact || exporting) return;
    setExporting(format);
    setExportError(false);
    try {
      await exportStudioOutput(format, buildArtifactExportDocument(artifact));
    } catch {
      setExportError(true);
    } finally {
      setExporting(null);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={artifact?.title ?? t('aiStudio.outputs.documentTitle')}
      size="xl"
      className="max-w-[min(96rem,calc(100vw-2rem))]"
    >
      {!artifact ? null : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleExport('pdf')}
              disabled={exporting != null}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-1.5 text-base text-[var(--color-surface-text)] hover:border-[var(--color-primary-bg)] disabled:opacity-50"
            >
              {exporting === 'pdf'
                ? t('aiStudio.outputs.exporting')
                : t('aiStudio.outputs.exportPdf')}
            </button>
            <button
              type="button"
              onClick={() => void handleExport('docx')}
              disabled={exporting != null}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-1.5 text-base text-[var(--color-surface-text)] hover:border-[var(--color-primary-bg)] disabled:opacity-50"
            >
              {exporting === 'docx'
                ? t('aiStudio.outputs.exporting')
                : t('aiStudio.outputs.exportWord')}
            </button>
            {exportError ? (
              <span className="text-sm text-[var(--color-error)]" role="alert">
                {t('aiStudio.outputs.exportFailed')}
              </span>
            ) : null}
          </div>

          {artifact.body.type === 'schedule' ? (
            <div className="flex flex-col gap-3 text-base text-[var(--color-surface-text)]">
              {artifact.body.objective && (
                <p>
                  <span className="font-medium">{t('aiStudio.outputs.objective')}: </span>
                  {artifact.body.objective}
                </p>
              )}
              <div className="max-h-[min(70vh,calc(90vh-10rem))] overflow-auto rounded border border-[var(--color-border)]">
                <table className="min-w-full text-left text-base">
                  <thead className="sticky top-0 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('aiStudio.outputs.colWhen')}</th>
                      <th className="px-3 py-2 font-medium">{t('aiStudio.outputs.colMachine')}</th>
                      <th className="px-3 py-2 font-medium">{t('aiStudio.outputs.colJob')}</th>
                      <th className="px-3 py-2 font-medium">{t('aiStudio.outputs.colDuration')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifact.body.rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-3 py-3 text-[var(--color-surface-text-secondary)]"
                        >
                          {t('aiStudio.outputs.emptySchedule')}
                        </td>
                      </tr>
                    ) : (
                      artifact.body.rows.map((row, i) => (
                        <tr
                          key={`${row.when}-${row.machine}-${i}`}
                          className="border-t border-[var(--color-border)]"
                        >
                          <td className="px-3 py-2 whitespace-nowrap">{row.when}</td>
                          <td className="px-3 py-2">{row.machine}</td>
                          <td className="px-3 py-2">{row.job}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.duration}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {artifact.body.scoreLine && (
                <p className="text-[var(--color-surface-text-secondary)]">
                  <span className="font-medium text-[var(--color-surface-text)]">
                    {t('aiStudio.outputs.scorecard')}:{' '}
                  </span>
                  {artifact.body.scoreLine}
                </p>
              )}
              {artifact.body.notes && artifact.body.notes.length > 0 && (
                <p className="text-[var(--color-surface-text-secondary)]">
                  {artifact.body.notes.join('; ')}
                </p>
              )}
              <p className="text-sm text-[var(--color-surface-text-secondary)]">
                {t('aiStudio.outputs.selectInChatHint')}
              </p>
            </div>
          ) : (
            renderStructuredBody(artifact)
          )}

          {Array.isArray(artifact.body._decision?.evidenceRefs) &&
          artifact.body._decision.evidenceRefs.length > 0 ? (
            <div className="rounded border border-[var(--color-border)] p-3">
              <p className="mb-2 text-sm font-medium text-[var(--color-surface-text)]">
                {t('aiStudio.outputs.evidenceSourcesTitle')}
              </p>
              <ul className="flex flex-col gap-1 text-sm text-[var(--color-surface-text-secondary)]">
                {artifact.body._decision.evidenceRefs.map((ref) => (
                  <li key={ref.id} className="break-all">
                    <span className="font-medium text-[var(--color-surface-text)]">{ref.id}</span>
                    {ref.label ? ` — ${ref.label}` : ''}
                    {ref.url ? (
                      <>
                        {' '}
                        <a
                          href={ref.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-[var(--color-info)] underline"
                        >
                          {ref.url}
                        </a>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
};
